import React, { useCallback, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-v5';
import Img from 'react-cool-img';
import queryString from 'query-string';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Collapse, Fade, IconButton, InputAdornment } from '@mui/material';
import { isNull } from 'lodash';
import { IAppState } from 'spw-redux/reducer';
import {
  beginAuth,
  clearEmail,
  login,
  resetAuthFlow,
  setEmail,
  setPassword,
} from 'spw-redux/modules/auth/actions';
import Warning from 'spw-static/images/red-warning-lg.svg';
import GreenCheck from 'spw-static/images/green-check-lg.svg';
import { IUser } from 'spw-redux/modules/user/types';
import { APIToken } from 'spw-redux/modules/auth/types';
import {
  LogoutReason,
  logoutReasonToMessageText,
} from 'spw-redux/modules/UI/messages/types';
import * as constants from '../../constants';

import {
  DialogBox,
  DialogCustom,
  FormColumns,
  GradientBackgroundContainer,
  Title,
  SecondaryButton,
  TextField,
  ButtonCustom,
  LogoutToast,
  ErrorMessage,
  PasswordResetSuccessMessage,
} from './styles';

interface ILogin {
  email: string;
  password: string;
  showPasswordField: boolean;
  apiToken: APIToken | null;
  error: string | null;
  remainingLoginAttempts?: number;
  user: IUser;
  logoutReason?: LogoutReason;
  updatePasswordSuccess?: boolean;
}

const SSOLogin = ({
  apiToken,
  user,
  email,
  password,
  showPasswordField,
  logoutReason,
  error,
  remainingLoginAttempts,
  updatePasswordSuccess,
  location,
}: ILogin & RouteComponentProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const passwordFieldRef = useRef<HTMLInputElement>(null);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const { redirect } = queryString.parse(location.search) as {
    redirect?: string;
  };

  const handleKeyPressedEmail: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (password && showPasswordField) {
          dispatch(login(email, password, false, redirect));
        } else {
          dispatch(beginAuth(email));
        }
      }
    },
    [dispatch, email, password, showPasswordField, redirect]
  );

  const handleKeyPressedPassword: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        dispatch(login(email, password, false, redirect));
      }
    },
    [dispatch, email, password, redirect]
  );

  const validEmail = email.match(constants.EMAIL_REGEX);
  const validPassword = !!password.length;
  const isValid = validEmail && validPassword;

  if (!isNull(apiToken) && user?.id !== null) return <Redirect to="/home" />;

  return (
    <GradientBackgroundContainer>
      {updatePasswordSuccess && (
        <PasswordResetSuccessMessage>
          <Img
            src={GreenCheck}
            alt="password-reset-succeeded"
            className="green-check"
          />
          <span>Your password has been reset.</span>
        </PasswordResetSuccessMessage>
      )}
      {logoutReason && (
        <LogoutToast
          style={{
            fontSize:
              logoutReason === LogoutReason.sessionSuperseded ? '16px' : '14px',
          }}
        >
          {logoutReasonToMessageText[logoutReason]}
        </LogoutToast>
      )}

      <DialogBox>
        <DialogCustom>
          <Title>Space Plan Wizard</Title>
          <FormColumns>
            <TextField
              autoFocus
              placeholder="Email"
              variant="outlined"
              data-testid="email"
              onKeyPress={handleKeyPressedEmail}
              value={email}
              onChange={(e) => {
                if (e.target.value) dispatch(setEmail(e.target.value));
                else {
                  dispatch(clearEmail());
                  dispatch(resetAuthFlow());
                }
              }}
              onFocus={() => dispatch(resetAuthFlow())}
              helperText=" "
            />
            <Collapse in={!showPasswordField}>
              <Fade in={!showPasswordField}>
                <ButtonCustom
                  variant="contained"
                  data-testid="auth-submit-email"
                  onClick={() => {
                    dispatch(beginAuth(email));
                    if (passwordFieldRef.current) {
                      setTimeout(() => passwordFieldRef.current.focus(), 500);
                    }
                  }}
                  disabled={!validEmail}
                >
                  Next
                </ButtonCustom>
              </Fade>
            </Collapse>
            <Collapse in={showPasswordField}>
              <Fade in={showPasswordField}>
                <div>
                  <TextField
                    fullWidth
                    placeholder="Password"
                    variant="outlined"
                    autoFocus
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    inputRef={passwordFieldRef}
                    data-testid="password"
                    onChange={(e) => dispatch(setPassword(e.target.value))}
                    helperText=" "
                    onKeyPress={handleKeyPressedPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            size="large"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <ErrorMessage
                    noLetterSpacing
                    bottom="68px"
                    display={remainingLoginAttempts !== 1 && 'none !important'}
                  >
                    <Img src={Warning} alt="warning" />
                    <div>
                      You will be locked out of your account on the next failed
                      login.
                    </div>
                  </ErrorMessage>

                  <ErrorMessage
                    noLetterSpacing
                    justifyContent="center"
                    bottom="68px"
                    display={remainingLoginAttempts !== 0 && 'none !important'}
                  >
                    <Img src={Warning} alt="warning" />
                    <Box marginLeft="8px">
                      You are locked out. Refresh and try again after five
                      minutes.
                    </Box>
                  </ErrorMessage>

                  <Box mt="32px" />
                  <ButtonCustom
                    variant="contained"
                    data-testid="auth-submit-password"
                    onClick={() =>
                      dispatch(login(email, password, false, redirect))
                    }
                    disabled={!isValid}
                  >
                    Login
                  </ButtonCustom>
                </div>
              </Fade>
            </Collapse>
          </FormColumns>
          {showPasswordField && (
            <Box mt="24px" mb="4px">
              <SecondaryButton to={`/${constants.FORGOT_PASSWORD}`}>
                Forgot Password?
              </SecondaryButton>
            </Box>
          )}
          <ErrorMessage bottom="24px">{error}</ErrorMessage>
        </DialogCustom>
      </DialogBox>
    </GradientBackgroundContainer>
  );
};

const mapStateToProps = (state: IAppState): ILogin => ({
  email: state.authReducer.email,
  password: state.authReducer.password,
  showPasswordField: state.authReducer.showPasswordField,
  apiToken: state.authReducer.apiToken,
  error: state.authReducer.error,
  remainingLoginAttempts: state.authReducer.remainingLoginAttempts,
  user: state.userReducer.user,
  logoutReason: state.messagesReducer.logoutReason,
  updatePasswordSuccess: state.authReducer.updatePasswordSuccess,
});

export default connect(mapStateToProps)(withRouter(SSOLogin));
